import { memo } from 'react';
import {
  Ban,
  Circle,
  CircleCheckBig,
  CircleDashed,
  Clock,
  Hand,
  LoaderCircle,
  OctagonAlert,
  Send,
  TriangleAlert,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { PillState, PillTone, ProgressFigure, ProgressSegments } from '../utils/picklistProgress';
import { qty } from '../utils/picklistProgress';

/**
 * PW-12 — the landing's shared visual vocabulary: soft-tint status pills, the stacked progress bar and
 * the legend that explains its segments.
 *
 * **Tokens only.** Every tint is `--success` / `--warning` / `--info` / `--destructive` / `--muted`, so all
 * three themes (light, dark, Industrial) get the right contrast from one definition. No raw Tailwind palette
 * classes, no hex.
 */

const TONE_CLASS: Record<PillTone, string> = {
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  info: 'bg-info/10 text-info',
  destructive: 'bg-destructive/10 text-destructive',
  secondary: 'bg-muted text-muted-foreground',
  muted: 'bg-muted/60 text-muted-foreground',
};

/**
 * Which glyph a pill wears, keyed by its LABEL rather than by a second enum.
 *
 * The state functions in `picklistProgress` already decide the whole pill; making them also carry an icon
 * name would put a rendering concern in a pure arithmetic module, and making the caller re-derive the state
 * to choose an icon would mean two ladders that can disagree. One lookup, one source.
 */
function pillIcon(label: string) {
  if (label === 'Cancelled') return Ban;
  if (label === 'Pending pick' || label === 'Pending') return Clock;
  if (label.startsWith('Picking') || label === 'Partial') return Hand;
  if (label === 'Picked') return CircleCheckBig;
  if (label === 'Needs reconciling' || label === 'Exceeds MRS qty' || label === 'Swap needed') return OctagonAlert;
  // PT-7 C1 / C3 — "1 needs a swap", "3 need a swap", "2 serials need a swap": a taken serial.
  if (label.endsWith('needs a swap') || label.endsWith('need a swap')) return OctagonAlert;
  if (label === 'Post failed') return TriangleAlert;
  if (label.startsWith('Posting')) return LoaderCircle;
  // PT-7 A6 — the Items row's in-flight pill, `{qty} in #{batchId}`: a batch on its way, so it spins too.
  if (/^\S+ in #\d+$/.test(label)) return LoaderCircle;
  if (label === 'Not posted') return CircleDashed;
  if (label === 'Partially posted' || label.startsWith('Posted') || label.endsWith('posted')) return Send;
  return Circle;
}

export const StatusPill = memo(function StatusPill({ state, className }: { state: PillState; className?: string }) {
  const Icon = pillIcon(state.label);
  // From the glyph, not the label: every in-motion pill wears LoaderCircle, and only those spin.
  const spins = Icon === LoaderCircle;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* The pill is the tooltip's own trigger, so the explanation is reachable by keyboard as well as
            hover — these labels ("Needs reconciling") are the only place the list explains itself. */}
        <span
          tabIndex={0}
          className={cn(
            'inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            TONE_CLASS[state.tone],
            className,
          )}
        >
          <Icon className={cn('h-3 w-3 shrink-0', spins && 'animate-spin motion-reduce:animate-none')} aria-hidden />
          {state.label}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{state.tooltip}</TooltipContent>
    </Tooltip>
  );
});

interface ProgressBarProps {
  segs: ProgressSegments;
  tooltip: string;
  /** 8px on a picklist row, 6px on an item sub-row. */
  size?: 'md' | 'sm';
  className?: string;
}

export const ProgressBar = memo(function ProgressBar({ segs, tooltip, size = 'md', className }: ProgressBarProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* NOT focusable, unlike the pills. `role="img"` + `aria-label` means a screen reader already
            reads the full breakdown in row order; adding a tab stop here would put two extra stops in
            front of every row to reach the same sentence. The pills keep theirs because "Needs
            reconciling" has no meaning without its tooltip. */}
        <div
          role="img"
          aria-label={tooltip}
          className={cn(
            'flex w-full overflow-hidden rounded-full bg-muted',
            size === 'md' ? 'h-2' : 'h-1.5',
            className,
          )}
        >
          <div className="h-full bg-success" style={{ width: `${segs.postedPct}%` }} />
          {/* Posting to NetSuite. Striped rather than solid so it reads as "in motion", and SUCCESS-tinted
              so it groups with the posted run it is joining rather than the picked run it has left. The
              stripe carries the meaning; its drift is dropped under prefers-reduced-motion. */}
          <div className="picklist-flight h-full" style={{ width: `${segs.inflightPct}%` }} />
          <div className="h-full bg-info" style={{ width: `${segs.pickedPct}%` }} />
        </div>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
});

/** Same tint as the bar segment each figure names — the dot IS the segment, not a decoration near it. */
const FIGURE_DOT: Record<ProgressFigure['key'], string> = {
  posted: 'bg-success',
  inflight: 'picklist-flight',
  picked: 'bg-info',
  // The bar's "still to pick" is its TRACK, `bg-muted`, which reads as a colour only because the track is
  // wide. At 8px on a card that is very nearly invisible, so the dot keeps the token and gains a ring.
  toPick: 'bg-muted ring-1 ring-inset ring-border',
};

/**
 * The bar's segments as numbers, directly beneath it.
 *
 * Replaces a prose sub-line ("None of the picked stock posted yet") that was the same sentence on almost
 * every row and carried no quantities. Proportion is the bar's job; this is magnitude, in the same order
 * and the same colours, so the two read as one object rather than two competing summaries.
 *
 * ♿ One `role="img"` for the whole row, labelled with the full breakdown sentence — the pattern the bar
 * above already uses. Per-dot tab stops would put four extra stops in front of every row to reach a
 * sentence the bar already reads out, and the dots are not controls.
 */
export const ProgressDots = memo(function ProgressDots({
  figures,
  tooltip,
}: {
  figures: ProgressFigure[];
  tooltip: string;
}) {
  // A picklist with nothing to pick at all has no partition to report, and four zeros say less than the
  // words do. A picklist that simply has not STARTED is a different case and keeps its figures — "0 · 0 ·
  // 40 still to pick" is exactly the row a supervisor is looking for.
  if (figures.every((f) => f.value === 0)) {
    return <p className="text-xs text-muted-foreground">Not started</p>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* gap-x-2, not gap-x-3. The indented ITEM row gets 265px against the parent's 289px, and three
            labelled figures need ~270px at gap-x-3 — so sub-rows wrapped to two lines by about five
            pixels while their parents sat on one. 4px back per gap is enough, and flex-wrap still
            catches a narrower viewport rather than clipping. */}
        <div
          role="img"
          aria-label={tooltip}
          className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground"
        >
          {figures.map((f) => (
            <span key={f.key} className="inline-flex items-center gap-1 whitespace-nowrap">
              <span className={cn('h-2 w-2 shrink-0 rounded-full', FIGURE_DOT[f.key])} aria-hidden />
              {/* The number is what you compare down a column, so it keeps tabular figures; the word is
                  prose and must not be forced onto the same fixed advance. */}
              <span className="tabular-nums">{qty(f.value)}</span> {f.label}
            </span>
          ))}
        </div>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
});

/**
 * The legend. **It ships with the bar or not at all** — a legend entry documenting a segment that never
 * renders is worse than shipping neither, so the committed swatch is drawn on the same condition as the
 * committed segment.
 */
export const ProgressLegend = memo(function ProgressLegend({ showCommitted }: { showCommitted: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <Swatch className="bg-success" label="Posted to NetSuite (MIS)" />
      <Swatch className="bg-info" label="Picked · awaiting posting" />
      {showCommitted && <Swatch className="picklist-flight" label="Posting to NetSuite" />}
      <Swatch className="bg-muted" label="Still to pick" />
    </div>
  );
});

function Swatch({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('h-2 w-5 rounded-full', className)} aria-hidden />
      {label}
    </span>
  );
}
